import React, { useContext, useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import {
  requestConfirmState,
  requestApproveEdits,
  requestDelistState,
  requestFinishProposeSite,
  requestNominateSite,
  requestReviewByRfp,
  requestCommentsData,
  deleteSiteRequest,
  requestDoesNotQualify,
  requestFinishEditOnlySite,
  deleteEditSiteOnlyRequest
} from 'api/site'
import Button from 'components/Button'
import CommentsIcon from 'assets/icons/comments-icon'
import SubmitIcon from 'assets/icons/submit-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import ArrowLeftLine from 'assets/icons/arrow-left-line'
import BlockIcon from 'assets/icons/block-icon'
import ReturnDraftIcon from 'assets/icons/return-draft-icon'
import CheckIcon from 'assets/icons/check-icon'
import RemoveIcon from 'assets/icons/remove-icon16px'
import NominateIcon from 'assets/icons/nominate-icon'
import DelistIcon from 'assets/icons/delist-icon'
import CommentSection from 'flow/siteProposal/components/comments/commentSection'
import { SiteContext } from 'providers/site'
import Modal from 'components/Modal'
import AutoSaveIcon from 'features/AutoSaveIcon'
import { ProfileContext } from 'providers/profile'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID } from 'constants/roles'
import { canReviewSites, canConfirmSites, getProfileRoleId } from 'utils/rolePermissions'
import { canSecretariatRemoveDraft } from 'utils/draftRemovalPermissions'
import FlowRequestModal from 'components/FlowRequestModal'
import ValidationProposalModal from './components/validationProposalModal'
import { useRequestQueue } from 'hooks/useRequestQueue'
import { useSiteProposalPermissions } from 'hooks/useSiteProposalPermissions'
import { showErrorToast } from 'utils/toast'

import {
  FooterButtonDivider,
  NewSiteFooterButtonWrapper,
  NewSiteFooterCenterPart,
  NewSiteFooterRightPart,
  NewSiteFooterWrapper
} from './style'
import SiteProposalConfirmation from './components/siteProposalConfirmation'
import SiteEditConfirmation from './components/siteEditConfirmation'
import { getUncompletedProposalSections } from 'flow/siteProposal/utils/getUncompletedProposalSections'
import { flushPendingSiteSaves } from 'flow/siteProposal/utils/pendingSiteSaveFlushes'
import { isAltitudeRangeValid } from 'flow/siteProposal/pages/details/utils/numericFieldHandlers'
import {
  SITE_STATUS_PROPOSED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { MySiteContext } from '../../../../providers/mySiteProvider'
import { buildAboutUrl, buildDetailsUrl, editOnly } from 'routing/constants'

const getUncompletedSection = getUncompletedProposalSections

// An out-of-range altitude cannot be saved, so the stored record the server validates can still
// look valid while the page shows the offending value. Re-check what the user is looking at.
const withAltitudeRangeValidation = (validation, site) => {
  if (!validation?.details || isAltitudeRangeValid(site?.lowestAltitude, site?.highestAltitude)) {
    return validation
  }

  return {
    ...validation,
    details: { ...validation.details, highestAltitude: false }
  }
}

const runPreProposalValidation = async ({ getSiteValidation, getEditOnlySiteValidation, isEditOnly, site }) => {
  await flushPendingSiteSaves()
  const validation = isEditOnly
    ? await getEditOnlySiteValidation()
    : await getSiteValidation({ showErrors: true })

  return withAltitudeRangeValidation(validation, site)
}

const shouldShowCommentsButton = ({ showComments, siteStatusId, roleId }) => {
  if (!showComments) {
    return false
  }

  if (siteStatusId === SITE_STATUS_EDIT_ONLY_ID) {
    return roleId === INDIVIDUAL_ROLE_ID || roleId === NCG_ROLE_ID
  }

  return true
}

const renderNominateAction = ({ setSiteToNominate }) => (
  <NewSiteFooterButtonWrapper>
    <Button
      type="primary"
      withIcon={true}
      height="40px"
      onClick={() => {
        setSiteToNominate(true)
      }}
    >
      <NominateIcon />
      <FormattedMessage id="nominate" />
    </Button>
  </NewSiteFooterButtonWrapper>
)

const getFooterStatusId = (site) => Number(site?.SiteStatusId ?? site?.siteStatusId)

const canRenderEditOnlyFooterActions = ({ site, profile, canProposeFromDraft, isLoadingPermissions }) => {
  const statusId = getFooterStatusId(site)
  const roleId = getProfileRoleId(profile)
  const profileId = profile?.id
  // Edit-only session owner OR parent proposer/co-proposer (WDKBA-435: not parent-draft-only).
  const isEditOnlyOwner =
    Boolean(profileId) &&
    (site?.proposerId === profileId || site?.ProposerId === profileId)
  return (
    statusId === SITE_STATUS_EDIT_ONLY_ID &&
    (roleId === INDIVIDUAL_ROLE_ID || roleId === NCG_ROLE_ID) &&
    (canProposeFromDraft || isEditOnlyOwner) &&
    !isLoadingPermissions
  )
}

const canRenderNominateFooterAction = ({ site, profile, canNominate, isLoadingPermissions }) => {
  const roleId = getProfileRoleId(profile)
  return (
    getFooterStatusId(site) === SITE_STATUS_REVIEWED_BY_RFP_ID &&
    (roleId === INDIVIDUAL_ROLE_ID || roleId === NCG_ROLE_ID) &&
    canNominate &&
    !isLoadingPermissions
  )
}

const canRenderProposedRfpFooterActions = ({ site, profile }) =>
  getFooterStatusId(site) === SITE_STATUS_PROPOSED_ID && canReviewSites(getProfileRoleId(profile))

const renderFooterActions = ({
  site,
  profile,
  openDeleteModal,
  setIsModalOpen,
  getSiteValidation,
  setIsEditOnlyModalOpen,
  getEditOnlySiteValidation,
  setProposalValidationSnapshot,
  setReviewedByRfp,
  setSiteToNominate,
  setSiteToApproveEdits,
  openModalToDoesNotQualify,
  openModalToDelist,
  setSiteToConfirm,
  canProposeFromDraft,
  canNominate,
  isLoadingPermissions
}) => {
  const statusId = getFooterStatusId(site)
  const roleId = getProfileRoleId(profile)

  if (statusId === SITE_STATUS_DRAFT_ID && canProposeFromDraft && !isLoadingPermissions) {
    return (
      <NewSiteFooterButtonWrapper>
        <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
          <RemoveIcon />
          <FormattedMessage id="deleteDraftBtn" />
        </Button>
        <Button
          dataTestId="submit-proposal-button"
          height="40px"
          withIcon={true}
          type="primary"
          onClick={async () => {
            const validation = await runPreProposalValidation({
              getSiteValidation,
              getEditOnlySiteValidation,
              isEditOnly: false,
              site
            })
            setProposalValidationSnapshot(validation)
            setIsModalOpen(true)
          }}
        >
          <SubmitIcon />
          <FormattedMessage id="submitProposal" />
        </Button>
      </NewSiteFooterButtonWrapper>
    )
  }

  // KBA Secretariat: same bottom-right Delete draft slot as proposers (no Submit).
  if (canSecretariatRemoveDraft(profile, site) && !isLoadingPermissions) {
    return (
      <NewSiteFooterButtonWrapper>
        <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
          <RemoveIcon />
          <FormattedMessage id="deleteDraftBtn" />
        </Button>
      </NewSiteFooterButtonWrapper>
    )
  }

  // Same layout as draft: Delete + Submit, no divider.
  if (canRenderEditOnlyFooterActions({ site, profile, canProposeFromDraft, isLoadingPermissions })) {
    return (
      <NewSiteFooterButtonWrapper>
        <Button height="40px" withIcon={true} type="delist" onClick={openDeleteModal}>
          <RemoveIcon />
          <FormattedMessage id="deleteEdits" />
        </Button>
        <Button
          dataTestId="submit-edits-button"
          height="40px"
          withIcon={true}
          type="primary"
          onClick={async () => {
            const validation = await runPreProposalValidation({
              getSiteValidation,
              getEditOnlySiteValidation,
              isEditOnly: true,
              site
            })
            setProposalValidationSnapshot(validation)
            setIsEditOnlyModalOpen(true)
          }}
        >
          <SubmitIcon />
          <FormattedMessage id="submitEdits" />
        </Button>
      </NewSiteFooterButtonWrapper>
    )
  }

  if (canRenderProposedRfpFooterActions({ site, profile })) {
    return (
      <>
        <FooterButtonDivider />
        <NewSiteFooterButtonWrapper>
          <Button
            height="40px"
            withIcon={true}
            type="error"
            onClick={() => {
              setReviewedByRfp({ isApproved: false })
            }}
          >
            <ReturnDraftIcon />
            <FormattedMessage id="returnToDraft" />
          </Button>
          <Button
            height="40px"
            withIcon={true}
            type="primary"
            onClick={() => {
              setReviewedByRfp({ isApproved: true })
            }}
          >
            <CheckIcon />
            <FormattedMessage id="approve" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (canRenderNominateFooterAction({ site, profile, canNominate, isLoadingPermissions })) {
    return renderNominateAction({ setSiteToNominate })
  }

  if (
    statusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID &&
    (canConfirmSites(roleId) || canReviewSites(roleId))
  ) {
    return (
      <>
        <FooterButtonDivider />
        <NewSiteFooterButtonWrapper>
          <Button
            type="error"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToApproveEdits({ ...site, isApproved: false })
            }}
          >
            <BlockIcon />
            <FormattedMessage id="rejectEdits" />
          </Button>
          <Button
            type="primary"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToApproveEdits({ ...site, isApproved: true })
            }}
          >
            <ConfirmIcon />
            <FormattedMessage id="approveEdits" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  if (statusId === SITE_STATUS_NOMINATED_ID && canConfirmSites(roleId)) {
    return (
      <>
        {!site.reassessmentFlag && (
          <>
            <FooterButtonDivider />
            <NewSiteFooterButtonWrapper>
              <Button type="delist" height="40px" withIcon={true} onClick={openModalToDoesNotQualify}>
                <DelistIcon />
                <FormattedMessage id="doesNotQualify" />
              </Button>
            </NewSiteFooterButtonWrapper>
          </>
        )}
        <FooterButtonDivider />
        {site.delist && (
          <NewSiteFooterButtonWrapper style={{ marginRight: '15px' }}>
            <Button type="delist" height="40px" withIcon={true} onClick={openModalToDelist}>
              <DelistIcon />
              <FormattedMessage id="delist" />
            </Button>
          </NewSiteFooterButtonWrapper>
        )}
        <NewSiteFooterButtonWrapper>
          <Button
            type="error"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToConfirm({ ...site, isApproved: false })
            }}
          >
            <BlockIcon />
            <FormattedMessage id="rejected" />
          </Button>
          <Button
            type="primary"
            height="40px"
            withIcon={true}
            onClick={() => {
              return setSiteToConfirm({ ...site, isApproved: true })
            }}
          >
            <ConfirmIcon />
            <FormattedMessage id="confirm" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </>
    )
  }

  return null
}

const Footer = () => {
  const history = useNavigate()
  const params = useParams()
  const { siteProposalFieldValidation, getSiteValidation, site, getMySite, getEditOnlySiteValidation, setSiteProposalFieldValidation } = useContext(SiteContext)
  const { getSitesCount, refreshSites } = useContext(MySiteContext)
  const { profile } = useContext(ProfileContext)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditOnlyModalOpen, setIsEditOnlyModalOpen] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [reviewedByRfp, setReviewedByRfp] = useState(false)
  const [siteToNominate, setSiteToNominate] = useState(false)
  const [siteToConfirm, setSiteToConfirm] = useState(false)
  const [siteToApproveEdits, setSiteToApproveEdits] = useState(false)
  const [isSiteToDelist, setIsSiteToDelist] = useState(false)
  const [isSiteDoesNotQualified, setIsSiteDoesNotQualified] = useState(false)
  const [commentsNumber, setCommentsNumber] = useState(0)
  const [isModalForDeletingOpen, setIsModalForDeletingOpen] = useState(false)
  const [proposalValidationSnapshot, setProposalValidationSnapshot] = useState(null)
  const { permissions, isLoadingPermissions, refreshPermissions } = useSiteProposalPermissions()
  const profileRoleId = getProfileRoleId(profile)
  const siteStatusId = getFooterStatusId(site)
  const isNcgUser = profileRoleId === NCG_ROLE_ID
  const validationForProposal = proposalValidationSnapshot || siteProposalFieldValidation
  const sections = getUncompletedSection(validationForProposal, site?.proposalPurpose)
  const isLoading = useRequestQueue()
  const showComments = (siteStatusId !== SITE_STATUS_DRAFT_ID && siteStatusId !== SITE_STATUS_CONFIRMED_ID && siteStatusId !== SITE_STATUS_DOES_NOT_QUALIFY) || site.isApproved === false

  const editOnlySite = siteStatusId === SITE_STATUS_EDIT_ONLY_ID || siteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID ? editOnly : null;
  const editOnlySiteStatus = siteStatusId === SITE_STATUS_EDIT_ONLY_ID || siteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId


  function goBack() {
    refreshSites()
    getSitesCount(true)
    getSitesCount(false)
    history('/sites', { replace: true })
  }

  function openDeleteModal() {
    setIsModalForDeletingOpen(true)
  }

  function closeDelistModal() {
    setIsSiteToDelist(false)
  }

  function closeDoesNotQualifyModal() {
    setIsSiteDoesNotQualified(false)
  }

  function openModalToDelist() {
    setIsSiteToDelist(true)
  }

  function openModalToDoesNotQualify() {
    setIsSiteDoesNotQualified(true)
  }
  async function deleteSite() {
    try {
      let data

      if (site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID && site.historyId) {
        data = await deleteEditSiteOnlyRequest(site.id, site.SiteStatusId)
      } else {
        data = await deleteSiteRequest(site.id, site.reassessmentFlag, site.historyId)
      }

      if (data?.error) {
        return showErrorToast(data.error, 'showBorder')
      }

      goBack()
    } catch (err) {
      showErrorToast(err, 'showBorder');
    }
  }

  async function delistSite() {
    try {
      const data = await requestDelistState(site.historyId, true);
      if (data.error) {
        return showErrorToast(data.error, 'showBorder');
      }
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function doesNotQualify() {
    try {
      const data = await requestDoesNotQualify(site.id);
      if (data.error) {
        return showErrorToast(data.error, 'showBorder');
      }
      goBack();
    } catch (error) {
      showErrorToast(error);
    }
  }

  async function proposeSite() {
    try {
      if (!isNcgUser) {
        const access = await refreshPermissions(site.id, params.siteHistoryId)
        if (!access.canProposeFromDraft) {
          showErrorToast('Only the proposer or a co-proposer can submit this draft.', 'showBorder')
          return
        }
      }
      await flushPendingSiteSaves()
      const response = await requestFinishProposeSite(site.id, params.siteHistoryId);
      if (response.success) {
        await getMySite();
        return
      }

      const validationResponse = { ...response, updated: true }
      setSiteProposalFieldValidation(validationResponse)
      setProposalValidationSnapshot(validationResponse)
      setIsModalOpen(true)
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function rfpAction(element, isApproved) {
    try {
      await requestReviewByRfp(element.id, params.siteHistoryId, isApproved);
      setReviewedByRfp(false);
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function nominateSite(element) {
    try {
      if (!isNcgUser) {
        const access = await refreshPermissions(element.id, params.siteHistoryId)
        if (!access.canNominate) {
          showErrorToast('Only the proposer or a co-proposer can nominate this site.', 'showBorder')
          setSiteToNominate(false)
          return
        }
      }
      await requestNominateSite(element.id, params.siteHistoryId);
      setSiteToNominate(false);
      await getMySite();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function confirmAction(element, isApproved) {
    try {
      const historyId = params.siteHistoryId ?? element.historyId ?? element.sitehistoryId
      const isReassessmentConfirm = Boolean(
        historyId || element.reassessmentFlag || params.siteHistoryId
      )
      if (isReassessmentConfirm && !historyId) {
        showErrorToast('Missing reassessment history identifier', 'showBorder')
        return
      }
      const response = await requestConfirmState(element.id, historyId, isApproved)
      if (response?.error) {
        return showErrorToast(
          typeof response.error === 'string' ? response.error : 'Failed to confirm assessment',
          'showBorder'
        )
      }
      setSiteToConfirm(false)
      goBack()
    } catch (error) {
      showErrorToast(error, 'showBorder')
    }
  }

  async function approveEditsAction(element, isApproved) {
    try {
      // element.id is SitesEditOnly PK on the edit-only workflow page.
      const parentSiteId = element?.siteId || params?.id || element?.id
      await requestApproveEdits(element.id, parentSiteId, isApproved);
      setSiteToApproveEdits(false);
      goBack();
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  async function getCommentsData() {
    if (showComments) {
      try {
        const data = await requestCommentsData(site.id, historyId, editOnlySite);
        if (data.error) return;
        setCommentsNumber(data.length);
      } catch (error) {
        showErrorToast(error, 'showBorder');
      }
    }
  }

  const proposeEditSite = async () => {
    try {
      await flushPendingSiteSaves()
      // Do not gate on parent-site canProposeFromDraft — edit-only creator/co-proposer
      // authorization is enforced by the finishEditOnly API (WDKBA-435).
      const response = await requestFinishEditOnlySite(site.id)
      if (response.success) {
        getMySite()
        const parentId = site?.siteId || params?.id || site?.id
        const editOnlyId = site?.id
        if (parentId && editOnlyId) {
          history(buildAboutUrl(parentId, editOnlyId, editOnly))
        }
      }
    } catch (error) {
      showErrorToast(error, 'showBorder');
    }
  }

  const goToFirstIncompleteSection = () => {
    const incomplete = getUncompletedSection(
      proposalValidationSnapshot || siteProposalFieldValidation,
      site?.proposalPurpose
    )
    const parentId = site?.siteId || params?.id || site?.id
    const editOnlyId = site?.id
    if (!editOnlySite || !parentId || !editOnlyId) {
      return
    }

    // About is the default edit-only tab; send users there first when incomplete.
    if (incomplete.includes('About')) {
      history(buildAboutUrl(parentId, editOnlyId, editOnly))
      return
    }

    const needsDetails = incomplete.some((section) => String(section).startsWith('Site Details'))
    if (needsDetails) {
      history(buildDetailsUrl(parentId, editOnlyId, editOnly))
    }
  }

  useEffect(() => {
    getCommentsData()
  }, [])

  useEffect(() => {
    const statusId = getFooterStatusId(site)
    const isEditOnlyWorkflow =
      statusId === SITE_STATUS_EDIT_ONLY_ID ||
      statusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID

    // Edit-only uses SitesEditOnly ids in the route; proposal-permissions only
    // understands parent Site / reassessment history ids. Resolve against parent.
    if (isEditOnlyWorkflow) {
      refreshPermissions(site?.siteId || params?.id || site?.id)
      return
    }

    refreshPermissions(site?.id, params.siteHistoryId)
  }, [site?.id, site?.siteId, site?.SiteStatusId, site?.siteStatusId, params.id, params.siteHistoryId, refreshPermissions])

  const showAutoSave =
    siteStatusId === SITE_STATUS_DRAFT_ID || siteStatusId === SITE_STATUS_EDIT_ONLY_ID
  const showCommentsButton = shouldShowCommentsButton({
    showComments,
    siteStatusId,
    roleId: profileRoleId
  })

  return (
    <NewSiteFooterWrapper>
      <div style={{ width: '35%' }}>
        <NewSiteFooterButtonWrapper onClick={goBack}>
          <Button height="40px" withIcon={true}>
            <ArrowLeftLine />
            <FormattedMessage id="backToSites" />
          </Button>
        </NewSiteFooterButtonWrapper>
      </div>
      <NewSiteFooterCenterPart>
        {showAutoSave && <AutoSaveIcon loading={isLoading} time={'saved'} />}
        {showCommentsButton && (
          <NewSiteFooterButtonWrapper>
            <Button
              type="secondary"
              height="40px"
              onClick={() => {
                setIsOpen(true)
              }}
              withIcon={true}
            >
              <CommentsIcon />
              <FormattedMessage id="Comments" /> ({commentsNumber})
            </Button>
          </NewSiteFooterButtonWrapper>
        )}
      </NewSiteFooterCenterPart>
      <NewSiteFooterRightPart>
        {renderFooterActions({
          site,
          profile,
          openDeleteModal,
          setIsModalOpen,
          getSiteValidation,
          setIsEditOnlyModalOpen,
          getEditOnlySiteValidation,
          setProposalValidationSnapshot,
          setReviewedByRfp,
          setSiteToNominate,
          setSiteToApproveEdits,
          openModalToDoesNotQualify,
          openModalToDelist,
          setSiteToConfirm,
          canProposeFromDraft: isNcgUser || permissions.canProposeFromDraft,
          canNominate: isNcgUser || permissions.canNominate,
          isLoadingPermissions
        })}
      </NewSiteFooterRightPart>
      <Modal
        width="416px"
        toggleModal={isEditOnlyModalOpen ? setIsEditOnlyModalOpen : setIsModalOpen}
        isOpen={(isModalOpen || isEditOnlyModalOpen) && sections.length > 0}
        modalTitleId="unfinishedProposal"
      >
        <ValidationProposalModal
          sections={sections}
          toggleModal={isEditOnlyModalOpen ? setIsEditOnlyModalOpen : setIsModalOpen}
          onContinue={isEditOnlyModalOpen ? goToFirstIncompleteSection : undefined}
        />
      </Modal>
      <Modal
        width="416px"
        toggleModal={setIsModalOpen}
        isOpen={isModalOpen && sections.length === 0}
        modalTitleId="Propose assessment?"
      >
        <SiteProposalConfirmation name={site.name} proposeSite={proposeSite} toggleModal={setIsModalOpen} />
      </Modal>

      <Modal
        width="416px"
        toggleModal={() => setIsEditOnlyModalOpen(false)} // Ensures modal can be toggled
        isOpen={isEditOnlyModalOpen && sections.length === 0}
        modalTitleId="Submit Edits?"
      >
        <SiteEditConfirmation
          proposeEditSite={proposeEditSite}
          name={site.name}
          toggleModal={() => setIsEditOnlyModalOpen(false)} // Ensure it closes when needed
        />
      </Modal>

      <Modal
        width="480px"
        height="94vh"
        isOpen={isOpen}
        toggleModal={setIsOpen}
        modalTitleId="commentBox"
        titleValue={commentsNumber === 0 ? '0' : commentsNumber}
        infoTooltip="commentsHint"
        zIndex="1070"
        label="leaveComment"
        padding="0"
        bottomBorder={false}
        paddingBottom="0"
        sideModal={true}
      >
        <CommentSection setCommentsNumber={setCommentsNumber} />
      </Modal>
      <Modal
        width="416px"
        isOpen={reviewedByRfp.isApproved}
        toggleModal={setReviewedByRfp}
        modalTitleId="approveForNomination"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setReviewedByRfp(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="approve"
          text={<FormattedMessage id="approveForNominationText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={reviewedByRfp.isApproved === false}
        toggleModal={setReviewedByRfp}
        modalTitleId="returnDraft"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setReviewedByRfp(false)
          }}
          confirmHandler={rfpAction}
          confirmBtnText="returnToDraft"
          text={<FormattedMessage id="returnFeedbackText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal width="416px" isOpen={siteToNominate} toggleModal={setSiteToNominate} modalTitleId="nominateAssessment">
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToNominate(false)
          }}
          confirmHandler={nominateSite}
          confirmBtnText="nominate"
          text={<FormattedMessage id="nominateSiteText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToConfirm.isApproved === true}
        toggleModal={setSiteToConfirm}
        modalTitleId="confirmAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToConfirm(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="confirmAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToApproveEdits.isApproved === true}
        toggleModal={setSiteToApproveEdits}
        modalTitleId="approveEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToApproveEdits(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="confirm"
          text={<FormattedMessage id="approveEditsText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToApproveEdits.isApproved === false}
        toggleModal={setSiteToApproveEdits}
        modalTitleId="rejectEdits"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToApproveEdits(false)
          }}
          confirmHandler={approveEditsAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectEditsText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal
        width="416px"
        isOpen={siteToConfirm.isApproved === false}
        toggleModal={setSiteToConfirm}
        modalTitleId="rejectAssessment"
      >
        <FlowRequestModal
          cancelHandler={() => {
            setSiteToConfirm(false)
          }}
          confirmHandler={confirmAction}
          confirmBtnText="rejectBtn"
          text={<FormattedMessage id="rejectAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal width="496px" isOpen={isSiteToDelist} toggleModal={closeDelistModal} modalTitleId="delistAssessment">
        <FlowRequestModal
          cancelHandler={closeDelistModal}
          confirmHandler={delistSite}
          confirmBtnText="delistBtn"
          text={<FormattedMessage id="delistAssessmentText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={false}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={isSiteDoesNotQualified}
        toggleModal={closeDoesNotQualifyModal}
        modalTitleId="doesNotQualifyModal"
      >
        <FlowRequestModal
          cancelHandler={closeDoesNotQualifyModal}
          confirmHandler={doesNotQualify}
          confirmBtnText="confirm"
          text={<FormattedMessage id="doesNotQualifyText" values={{ siteName: <b>{site.name}</b> }} />}
          item={site}
          approve={true}
        />
      </Modal>
      <Modal
        width="496px"
        isOpen={isModalForDeletingOpen}
        toggleModal={setIsModalForDeletingOpen}
        modalTitleId={site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID ? 'deleteEditSiteOnly' : 'deleteDraft'}
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsModalForDeletingOpen(false)
          }}
          confirmHandler={deleteSite}
          confirmBtnText="yesDelete"
          text={
            site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID ? (
              <FormattedMessage id="editSiteForDeletingBody" values={{ siteName: <b>{site.name}</b> }} />
            ) : (
              <FormattedMessage id="siteForDeletingBody" values={{ siteName: <b>{site.name}</b> }} />
            )
          }
          item={site}
          approve={false}
        />
      </Modal>
    </NewSiteFooterWrapper>
  )
}

export default Footer
